import axios from 'axios';
import i18n from '@/i18n';
import { loadLocalEnglishMessages, mergeWithLocalFallback } from '@/i18n/catalog';

const B2C_LOCALE_KEY = 'commovis.uiLanguage';

const getDefaultState = () => ({
  locale: 'en',
  uiLanguages: ['en'],
  catalogLoaded: false,
  catalogSource: 'local',
});

const state = getDefaultState();

const getters = {
  locale: (s) => s.locale,
  uiLanguages: (s) => s.uiLanguages,
  catalogLoaded: (s) => s.catalogLoaded,
  hasMultipleLanguages: (s) => Array.isArray(s.uiLanguages) && s.uiLanguages.length > 1,
};

const normalizeUiLanguages = (languages) => {
  const list = Array.isArray(languages) ? languages.filter(Boolean) : [];
  if (!list.includes('en')) list.unshift('en');
  return [...new Set(list)];
};

const resolveLocale = (preferred, uiLanguages) => {
  const langs = normalizeUiLanguages(uiLanguages);
  if (preferred && langs.includes(preferred)) return preferred;
  return 'en';
};

const readB2cLocale = () => {
  try {
    return localStorage.getItem(B2C_LOCALE_KEY);
  } catch (e) {
    return null;
  }
};

const writeB2cLocale = (lang) => {
  try {
    localStorage.setItem(B2C_LOCALE_KEY, lang);
  } catch (e) {
    // ignore
  }
};

const actions = {
  async bootstrap({ commit, dispatch, rootState }) {
    const tenant = rootState.tenant?.tenantinfo || {};
    const uiLanguages = normalizeUiLanguages(tenant.uiLanguages || ['en']);
    commit('SET_UI_LANGUAGES', uiLanguages);

    const isB2c = tenant.b2c === true;
    const isAuthenticated = rootState.auth?.isAuthenticated === true;
    let preferred = 'en';

    if (isAuthenticated && rootState.userPreferences?.userPreferences?.Language) {
      preferred = rootState.userPreferences.userPreferences.Language;
    } else if (isB2c || !isAuthenticated) {
      preferred = readB2cLocale() || preferred;
    }

    const locale = resolveLocale(preferred, uiLanguages);
    commit('SET_LOCALE', locale);

    await dispatch('loadCatalog', { locale, tenantId: tenant.id });
    i18n.global.locale.value = locale;

    if (isAuthenticated && locale !== rootState.userPreferences?.userPreferences?.Language) {
      dispatch('userPreferences/updateLanguage', locale, { root: true }).catch(() => {});
    }
  },

  async loadCatalog({ commit }, { locale, tenantId }) {
    const fallback = loadLocalEnglishMessages();
    i18n.global.setLocaleMessage('en', fallback);
    i18n.global.fallbackLocale.value = 'en';

    if (!tenantId) {
      commit('SET_CATALOG_LOADED', { source: 'local' });
      if (locale !== 'en') {
        i18n.global.setLocaleMessage(locale, fallback);
      }
      return fallback;
    }

    try {
      const response = await axios.get('v2/ui-i18n/catalog', {
        params: { lang: locale, tenantId },
      });
      const remote = response.data?.messages || {};
      const messages = mergeWithLocalFallback(fallback, remote);
      i18n.global.setLocaleMessage(locale, messages);
      if (response.data?.uiLanguages) {
        commit('SET_UI_LANGUAGES', normalizeUiLanguages(response.data.uiLanguages));
      }
      commit('SET_CATALOG_LOADED', { source: 'remote' });
      return messages;
    } catch (error) {
      console.warn('UI i18n catalog fetch failed, using local EN pack', error);
      if (locale !== 'en') {
        i18n.global.setLocaleMessage(locale, fallback);
      }
      commit('SET_CATALOG_LOADED', { source: 'local' });
      return fallback;
    }
  },

  async setLocale({ commit, dispatch, state, rootState }, locale) {
    const resolved = resolveLocale(locale, state.uiLanguages);
    commit('SET_LOCALE', resolved);

    const tenantId = rootState.tenant?.tenantinfo?.id;
    await dispatch('loadCatalog', { locale: resolved, tenantId });
    i18n.global.locale.value = resolved;

    const isB2c = rootState.tenant?.tenantinfo?.b2c === true;
    const isAuthenticated = rootState.auth?.isAuthenticated === true;

    if (isB2c || !isAuthenticated) {
      writeB2cLocale(resolved);
    }
    if (isAuthenticated) {
      dispatch('userPreferences/updateLanguage', resolved, { root: true }).catch(() => {});
    }
  },
};

const mutations = {
  SET_LOCALE(s, locale) {
    s.locale = locale;
  },
  SET_UI_LANGUAGES(s, uiLanguages) {
    s.uiLanguages = normalizeUiLanguages(uiLanguages);
  },
  SET_CATALOG_LOADED(s, { source }) {
    s.catalogLoaded = true;
    s.catalogSource = source;
  },
  RESET_STATE(s) {
    Object.assign(s, getDefaultState());
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export { B2C_LOCALE_KEY, resolveLocale, normalizeUiLanguages };
